// What a screen reader is told about the open conversation (PARITY P255): new messages from others,
// politely; the reader's own send failing, assertively. Web-only (the natives use the platform's
// accessibility notifications); pure, so the rule is tested without a DOM.
import { PostState } from './records';

export interface AnnouncePost {
  id: string;
  userId: string;
  state: PostState;
  message: string;
  rootId: string | null;
  type: string;
  pendingId?: string | null;
}

export interface AnnounceState {
  channelId: string;
  seen: Set<string>;
  failed: Set<string>;
}

export interface AnnounceStep {
  state: AnnounceState;
  /** Others' new messages since the last step, oldest first (empty on a channel's first look). */
  arrived: AnnouncePost[];
  /** How many of the reader's own sends turned FAILED since the last step. */
  failedNow: number;
}

/**
 * One step: the first look at a channel only remembers what is there (opening a conversation is not
 * "new messages"); later looks report others' new ROOT messages that are delivered (no system notices,
 * no echoes of the reader's own), and own posts that newly failed.
 */
export function step(prev: AnnounceState | null, channelId: string, posts: readonly AnnouncePost[], me: string): AnnounceStep {
  const fresh = !prev || prev.channelId !== channelId;
  const seen = new Set<string>();
  const failed = new Set<string>();
  const arrived: AnnouncePost[] = [];
  let failedNow = 0;
  for (const p of posts) {
    seen.add(p.id);
    if (p.pendingId) seen.add(p.pendingId); // a confirmed send renames its row; it was already seen
    if (p.userId === me && p.state === PostState.Failed) {
      failed.add(p.id);
      if (!fresh && !prev.failed.has(p.id)) failedNow++;
    }
    if (fresh || prev.seen.has(p.id) || (p.pendingId && prev.seen.has(p.pendingId))) continue;
    if (p.userId === me || p.state !== PostState.Synced || p.rootId || p.type.startsWith('system_')) continue;
    arrived.push(p);
  }
  return { state: { channelId, seen, failed }, arrived, failedNow };
}

export const LiveAnnouncements = { step };
